import { Outlet, useNavigate } from 'react-router-dom'
import { Page } from '@/common/layouts/page.tsx'
import React, { useEffect, useMemo } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks.ts'
import {
  reloadThemeStyle,
  selectTheme,
} from '@/features/setting/setting.slice.ts'
import { Theme } from '@/features/setting/setting.def.ts'
import {
  getDarkComponents,
  getDarkToken,
  getLightComponents,
  getLightToken,
} from '@/styles/antd/config-token/token.ts'
import {
  requestUserInfo,
  selectUser,
  setUserInfo,
} from '@/features/user/user.slice.ts'
import { useLazyGetUserInfoQuery } from '@/apis/auth/auth.api.ts'
import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from '@/utils/local-storage.ts'
import { IdeyaFullScreenSpinner } from '@/common/components/ideya-spinner.tsx'
import ConfigProvider from 'antd/es/config-provider/index'
import theme from 'antd/es/theme/index'
import { useMatomoTrack } from '@/matomo/use-matomo-track.ts'
import { useFixedBreakpoint } from '@/common/hooks/use-is-mobile.ts'
import { NEW_RELIC, NEW_RELIC_AUTH_EVENT, newRelic } from '@/utils/new-relic.ts'
import { GlobalNotificationHolder } from '@/common/components/global-notification-holder.tsx'

// Root layout for the Auth0 external mode. On reload the in-memory Auth0 session
// is gone, so the app session is restored via /verify using the stored
// apiAccessToken instead of Auth0's isAuthenticated. The Auth0 login page is
// used only when there is no app token or verification fails.
export const Auth0Root: React.FC = () => {
  const navigate = useNavigate()
  const colorTheme = useAppSelector(selectTheme)
  const user = useAppSelector(selectUser)
  const dispatch = useAppDispatch()
  const [getUserInfo, { data: userInfoData, isLoading, isError }] =
    useLazyGetUserInfoQuery()
  const { isLoading: isAuth0Loading } = useAuth0()
  useMatomoTrack()
  const screens = useFixedBreakpoint()

  const token = useMemo(
    () => (colorTheme === Theme.Dark ? getDarkToken() : getLightToken()),
    [colorTheme]
  )

  const components = useMemo(
    () =>
      colorTheme === Theme.Dark ? getDarkComponents() : getLightComponents(),
    [colorTheme]
  )

  useEffect(() => {
    document.documentElement.setAttribute(
      'data-theme',
      colorTheme === Theme.Dark ? Theme.Dark : Theme.Light
    )
    reloadThemeStyle()
  }, [colorTheme])

  const hasApiToken = !!getLocalStorageItem('apiAccessToken')

  useEffect(() => {
    if (userInfoData) {
      newRelic.recordCustomEvent(NEW_RELIC_AUTH_EVENT.verifyTokenSucceeded, {
        element: NEW_RELIC.AUTH.verifyToken,
      })
      dispatch(setUserInfo(userInfoData.data))
    }

    if (hasApiToken && !user && !isLoading && !userInfoData) {
      dispatch(requestUserInfo())
      getUserInfo()
    }
  }, [userInfoData, dispatch, user, hasApiToken, isLoading, getUserInfo])

  // Fall back to Auth0 login when there is no session to restore: either no app
  // token, or the stored token failed verification.
  useEffect(() => {
    if (user || (!hasApiToken && isAuth0Loading)) return

    if (!hasApiToken) {
      navigate('/login')
    } else if (!isLoading && isError) {
      newRelic.recordCustomEvent(NEW_RELIC_AUTH_EVENT.verifyTokenFailed, {
        element: NEW_RELIC.AUTH.verifyToken,
      })
      newRelic.noticeError(new Error('verify failed, falling back to login'), {
        context: 'auth0RootVerifyToken',
      })
      removeLocalStorageItem('apiAccessToken')
      navigate('/login')
    }
  }, [user, hasApiToken, isAuth0Loading, isLoading, isError, navigate])

  // Restore the app session on mount only when an app token exists, so we never
  // fire /verify (and get a 401) when there is nothing to restore.
  useEffect(() => {
    if (!user || isLoading) return

    if (location.pathname.startsWith('/idea')) {
      navigate(`/idea${location.search}`)
    } else if (location.pathname === '/') {
      navigate('/idea')
    } else {
      navigate(location.pathname + location.search)
    }
  }, [user, hasApiToken, dispatch, getUserInfo, isLoading, navigate])

  if ((!hasApiToken && isAuth0Loading) || (hasApiToken && isLoading) || !user) {
    return <IdeyaFullScreenSpinner />
  }

  return (
    <ConfigProvider
      theme={{
        cssVar: true,
        token,
        components,
        algorithm:
          colorTheme === Theme.Dark
            ? theme.darkAlgorithm
            : theme.defaultAlgorithm,
      }}
      componentSize={screens.md ? 'small' : 'middle'}
    >
      <GlobalNotificationHolder />
      <div className={'w-full h-full'}>
        <Page userInfo={user}>
          <Outlet />
        </Page>
      </div>
    </ConfigProvider>
  )
}
